import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import "./styles.css";

import { useAuth } from "./hooks/useAuth";
import { useLeads } from "./hooks/useLeads";
import { useCredits } from "./hooks/useCredits";
import { useSubscription } from "./hooks/useSubscription";
import { useGodMode } from "./hooks/useGodMode";
import { useTheme } from "./hooks/useTheme";
import { ToastProvider, useToast } from "./hooks/useToast";
import { UnreadLeadsProvider } from "./hooks/UnreadLeadsContext";
import { supabase } from "./supabaseClient";
import { shouldHoldTradieDashboardForSubscription } from "./utils/tradieWorkspace";

import Header from "./components/common/Header";
import Card from "./components/common/Card";
import Landing from "./pages/Landing";
import CustomerApp from "./components/customer/CustomerApp";
import CustomerMessages from "./components/customer/CustomerMessages";
import CustomerSettings from "./components/customer/CustomerSettings";
import CustomerAuth from "./components/customer/CustomerAuth";
import CustomerQuoteView from "./components/customer/CustomerQuoteView";
import CustomerInvoiceView from "./components/customer/CustomerInvoiceView";
import DirectQuotePage from "./components/customer/DirectQuotePage";
import TradieAuth from "./components/tradie/TradieAuth";
import TradieApp from "./components/tradie/TradieApp";
import TradieSettings from "./components/tradie/TradieSettings";
import OnboardingFlow from "./components/tradie/OnboardingFlow";
import MobileBottomNav from "./components/common/MobileBottomNav";
import CommandPalette from "./components/common/CommandPalette";
import UpdateBanner from "./components/common/UpdateBanner";
import InstallAppBanner from "./components/common/InstallAppBanner";
import GodModePanel from "./components/admin/GodModePanel";
import ResetPasswordPage from "./components/common/ResetPasswordPage";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(err, info) {
    console.error("[RemoteQuote] Uncaught error:", err, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: "40px 20px", textAlign: "center" }}>
          <h2>Something went wrong</h2>
          <p style={{ color: "#6b7280", marginBottom: "16px" }}>
            An unexpected error occurred. Please refresh the page.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              background: "var(--primary)", color: "#fff", border: "none",
              borderRadius: "12px", padding: "12px 24px", fontSize: "0.95rem",
              fontWeight: 700, cursor: "pointer",
            }}
          >
            Refresh page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function App() {
  return (
    <ErrorBoundary>
      <ToastProvider>
        <UnreadLeadsProvider>
          <AppInner />
          {/* Light/dark toggle is now inside Settings (per Shannon, 2026-04-28).
              Floating FAB removed from main UI. */}
          <UpdateBanner />
          <InstallAppBanner />
        </UnreadLeadsProvider>
      </ToastProvider>
    </ErrorBoundary>
  );
}

function AppInner() {
  const [currentView, setCurrentView] = useState("landing");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [quoteToken, setQuoteToken] = useState(null);
  const [invoiceToken, setInvoiceToken] = useState(null);
  // God-mode panel controlled state — lifted here so the legacy floating FAB
  // can be removed and the panel is now opened from Settings → God Mode.
  const [godModeOpen, setGodModeOpen] = useState(false);
  // Public share-link flow — business_id is intentionally queryable by unauthenticated users.
  // Ensure the profiles RLS policy restricts columns returned to unauthenticated callers (exclude phone, email).
  const [tradieId, setTradieId] = useState(
    () => new URLSearchParams(window.location.search).get("tradie") || null
  );
  const [directBusiness, setDirectBusiness] = useState(null);
  const [directBusinessLoading, setDirectBusinessLoading] = useState(
    () => !!new URLSearchParams(window.location.search).get("tradie")
  );

  const { showToast } = useToast();
  const { session, profile, businessId, role, authLoading, handleSignOut } = useAuth();

  const isCustomer = role === "customer";
  // Treat both 'tradie' and 'business' as tradie for routing/permission
  // purposes — some older accounts were created with role='business' and we
  // need them to land on the tradie dashboard, not the customer one.
  const isTradie = role === "tradie" || role === "business";

  // (Diagnostic [RemoteQuote auth] console.log removed -- it fires on
  // every useAuth re-render and was producing 30+ log lines per minute
  // in production. Restore behind a feature flag if needed for debug.)

  const {
    requests,
    leadsLoading,
    submitLead,
    createOnsiteQuoteLead,
    updateRequestStatus,
    saveQuote,
    refreshMainAppData,
    handleLeadUnlocked,
    loadLeadDetails,
    bookJob,
    dismissLead,
    undismissLead,
    dismissedLeadIds,
    updateLeadStage,
    archiveLead,
    unarchiveLead,
    updateLeadContactDetails,
  } = useLeads({ session: isTradie ? session : null, businessId, showToast });

  const { creditBalance, unlockedLeadIds, unlockLead } = useCredits({
    businessId: isTradie ? businessId : null,
    onLeadUnlocked: handleLeadUnlocked,
    showToast,
  });

  const subscriptionRaw = useSubscription({ businessId: isTradie ? businessId : null });
  const { isGodMode } = useGodMode();

  // Merge isGodMode into the subscription object so Header and other consumers can read it.
  const subscription = { ...subscriptionRaw, isGodMode };

  // Apply tier-matched visual theme (CSS variables + body background).
  // Only runs for tradie views — passes null for customers and unauthenticated users.
  const theme = useTheme(isTradie ? subscription : null);

  // Pricing modal — lifted here so the Header trial sub-bar can trigger it from any view
  const [showPricing, setShowPricing] = useState(false);

  // Deep link parsed once from ?lead=...&tab=... — set by the email's
  // "View your message" CTA so the customer dashboard opens with the
  // matching request expanded and the per-quote chat thread visible. The
  // value is consumed by CustomerDashboard on its first render after this
  // value lands; the URL is cleared as soon as we apply it.
  const [messageDeepLink, setMessageDeepLink] = useState(() => {
    if (typeof window === "undefined") return null;
    const params = new URLSearchParams(window.location.search);
    const leadId = params.get("lead");
    const tab = params.get("tab");
    if (!leadId) return null;
    return { leadId, tab: tab || null };
  });

  // Show onboarding when a tradie logs in for the first time
  const [showOnboarding, setShowOnboarding] = useState(false);
  // Guards against race: when onComplete fires the profile refetch may briefly still show
  // onboarding_completed=false, which would re-trigger the effect and re-open onboarding.
  const justCompletedOnboarding = React.useRef(false);
  // Sync showOnboarding whenever profile loads or changes
  React.useEffect(() => {
    if (justCompletedOnboarding.current) {
      // Profile refetch in flight after onboarding completion — don't re-open
      justCompletedOnboarding.current = false;
      return;
    }
    if (isTradie && profile && !profile.onboarding_completed) {
      setShowOnboarding(true);
    } else {
      setShowOnboarding(false);
    }
  }, [isTradie, profile]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setQuoteToken(params.get("quote") || null);
    setInvoiceToken(params.get("invoice") || null);
  }, []);

  // ── Password-recovery flow ──────────────────────────────────────────────
  // Supabase sends reset-password emails with a URL fragment that looks like
  //   #access_token=…&type=recovery
  // The Supabase client auto-consumes the token and fires a PASSWORD_RECOVERY
  // auth event. We listen for BOTH (initial hash on load + the event) so the
  // recovery screen shows reliably regardless of client-library version.
  const [passwordRecovery, setPasswordRecovery] = useState(
    () => typeof window !== "undefined"
      && window.location.hash.includes("type=recovery")
  );
  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") setPasswordRecovery(true);
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!tradieId) {
      setDirectBusiness(null);
      return;
    }
    let cancelled = false;
    setDirectBusinessLoading(true);
    // Use the SECURITY DEFINER RPC instead of a direct profiles query.
    // The profiles RLS policy only permits auth.uid()=id reads, so an
    // unauthenticated customer visiting ?tradie=<id> can never read the
    // tradie's profile directly. The RPC exposes only the curated
    // column set safe for public consumption (no phone/email/licences).
    supabase
      .rpc("get_public_business_profile", { p_business_id: tradieId })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          showToast("Failed to load business profile.", "error");
          setDirectBusiness(null);
        } else {
          // RPC returns an array (RETURNS TABLE); take the first row.
          setDirectBusiness((Array.isArray(data) && data[0]) || null);
        }
        setDirectBusinessLoading(false);
      });
    return () => { cancelled = true; };
  }, [tradieId, showToast]);

  // Role-based post-login routing. Fires once when `isCustomer` or `isTradie`
  // transitions to true — so a user who later navigates back to "landing"
  // from their dashboard isn't yanked back. Routing is driven strictly by
  // the profile.role value, NOT by which auth page they submitted from, so
  // a customer who mistypes their creds on TradieAuth still lands on the
  // customer dashboard (and vice versa).
  useEffect(() => {
    if (isCustomer) {
      const params = new URLSearchParams(window.location.search);
      const view = params.get("view");
      const leadParam = params.get("lead");
      // Deep-link: ?lead=ID&tab=messages — open the dashboard with the
      // matching request card expanded. Falls through to the legacy
      // ?view=messages handler for older email links.
      if (leadParam) {
        setCurrentView("customer");
        const url = new URL(window.location.href);
        url.searchParams.delete("lead");
        url.searchParams.delete("tab");
        window.history.replaceState({}, "", url.toString());
      } else if (view === "messages") {
        setCurrentView("customer-messages");
        const url = new URL(window.location.href);
        url.searchParams.delete("view");
        window.history.replaceState({}, "", url.toString());
      } else {
        setCurrentView("customer");
      }
    } else if (isTradie) {
      setCurrentView("tradie");
    }
  }, [isCustomer, isTradie]); // intentional: role transitions only, not every view change

  // Settings is tradie-only — redirect anyone else who lands on it
  useEffect(() => {
    if (currentView === "settings" && !isTradie) {
      setCurrentView("landing");
    }
  }, [currentView, isTradie]);

  function getCustomerQuoteLink(token) {
    return `${window.location.origin}/?quote=${token}`;
  }

  function getCustomerInvoiceLink(token) {
    return `${window.location.origin}/?invoice=${token}`;
  }

  async function handleSubmitRequest(request) {
    // Step 6 (StepReview) now creates/signs into a customer account BEFORE
    // submission and passes the resulting user id as request._customerUserIdOverride.
    // Using the override avoids a race where useAuth's profile fetch hasn't
    // propagated yet — without it customer_user_id would be null and the lead
    // would only be discoverable via the guest-email fallback in CustomerDashboard.
    const freshCustomerUserId = request._customerUserIdOverride || null;
    const customerId =
      freshCustomerUserId || (isCustomer ? session?.user?.id : null);

    const submitResult = await submitLead(request, customerId, tradieId);
    const saved = submitResult?.ok ? submitResult.request : null;
    if (saved) {
      if (tradieId) {
        // Clear the ?tradie= param and direct quote state after submission so
        // the next render doesn't re-mount DirectQuotePage with a fresh form.
        const url = new URL(window.location.href);
        url.searchParams.delete("tradie");
        window.history.replaceState({}, "", url.toString());
        setTradieId(null);
        setDirectBusiness(null);
        // If the customer just signed up (or was already a customer) route
        // them to the customer dashboard so they see their submitted lead +
        // can track its status. Otherwise (anonymous-only submit) fall back
        // to the landing page.
        if (isCustomer || freshCustomerUserId) {
          setCurrentView("customer");
        } else {
          setCurrentView("landing");
        }
      } else if (isCustomer || freshCustomerUserId) {
        // Existing customer OR just-signed-up customer — route to the customer
        // view so CustomerApp shows the post-submit "View dashboard" screen
        // regardless of whether useAuth has finished loading the profile yet.
        setCurrentView("customer");
      } else if (session) {
        setCurrentView("tradie");
      } else {
        // anonymous submit: go back to landing
        setCurrentView("landing");
      }
    }
    return submitResult;
  }

  function handleBackToApp() {
    const url = new URL(window.location.href);
    url.searchParams.delete("quote");
    url.searchParams.delete("invoice");
    window.history.replaceState({}, "", url.toString());
    setQuoteToken(null);
    setInvoiceToken(null);
  }

  // ── Password recovery view — takes priority over every other screen so a
  // reset-link user always lands on the "set new password" form even if
  // they already had a session open in another tab.
  if (passwordRecovery) {
    return (
      <div className="app-shell">
        <main className="container">
          <ResetPasswordPage onDone={() => setPasswordRecovery(false)} />
        </main>
      </div>
    );
  }

  // ── Quote token view (no auth required) ──────────────────────────────────
  if (quoteToken) {
    return (
      <div className="app-shell">
        <main className="container">
          <CustomerQuoteView
            token={quoteToken}
            onBackToApp={handleBackToApp}
            refreshMainApp={refreshMainAppData}
            session={session}
          />
        </main>
      </div>
    );
  }

  // Invoice token view (no auth required). Same shape as the quote
  // token route -- the public_token lets unauthenticated customers see
  // their invoice without needing an account.
  if (invoiceToken) {
    return (
      <div className="app-shell">
        <main className="container">
          <CustomerInvoiceView
            token={invoiceToken}
            onBackToApp={handleBackToApp}
            refreshMainApp={refreshMainAppData}
            session={session}
          />
        </main>
      </div>
    );
  }

  // ── Direct tradie quote link ──────────────────────────────────────────────
  if (tradieId) {
    return (
      <div className="app-shell">
        <main className="container">
          <DirectQuotePage
            business={directBusiness}
            loading={directBusinessLoading}
            onSubmitRequest={handleSubmitRequest}
            session={session}
            role={role}
            profile={profile}
            businessId={businessId}
          />
        </main>
      </div>
    );
  }

  // ── Loading ───────────────────────────────────────────────────────────────
  if (authLoading) {
    return (
      <div className="app-shell">
        <main className="container">
          <Card>
            <h2>Loading account...</h2>
            <p className="muted">
              Please wait while we load your session and business profile.
            </p>
          </Card>
        </main>
      </div>
    );
  }

  // ── Session exists but no profile row found ───────────────────────────────
  // This only happens when profile creation failed (RPC error, missing table,
  // etc.). Give the user an escape hatch — never hang here.
  if (session && !role) {
    return (
      <div className="app-shell">
        <main className="container">
          <ProfileSetupPending onSignOut={handleSignOut} />
        </main>
      </div>
    );
  }

  // ── No session: show landing with auth options ────────────────────────────
  if (!session) {
    return (
      <div className="app-shell">
        <Header
          currentView={currentView}
          setCurrentView={setCurrentView}
          mobileOpen={mobileOpen}
          setMobileOpen={setMobileOpen}
          onSignOut={null}
          role={null}
        />
        <main className="container">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentView}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
            >
              {currentView === "landing" && (
                <Landing
                  setCurrentView={setCurrentView}
                  totalRequests={0}
                  profile={null}
                  onCustomerSignIn={() => setCurrentView("customer-auth")}
                />
              )}
              {currentView === "customer" && (
                <CustomerApp
                  onSubmitRequest={handleSubmitRequest}
                  session={null}
                  role={null}
                />
              )}
              {currentView === "customer-auth" && <CustomerAuth />}
              {currentView === "tradie" && <TradieAuth />}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    );
  }

  // ── Authenticated customer ────────────────────────────────────────────────
  if (isCustomer) {
    return (
      <div className="app-shell">
        <Header
          currentView={currentView}
          setCurrentView={setCurrentView}
          mobileOpen={mobileOpen}
          setMobileOpen={setMobileOpen}
          onSignOut={handleSignOut}
          role={role}
        />
        <main className="container">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentView}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
            >
              {currentView === "landing" ? (
                <Landing
                  setCurrentView={setCurrentView}
                  totalRequests={0}
                  profile={profile}
                  onCustomerSignIn={() => setCurrentView("customer-auth")}
                />
              ) : currentView === "customer-messages" ? (
                <CustomerMessages
                  session={session}
                  onBack={() => setCurrentView("customer")}
                />
              ) : currentView === "customer-settings" ? (
                <CustomerSettings
                  session={session}
                  profile={profile}
                  onBack={() => setCurrentView("customer")}
                  onProfileUpdated={() => window.location.reload()}
                />
              ) : (
                <CustomerApp
                  onSubmitRequest={handleSubmitRequest}
                  session={session}
                  role={role}
                  profile={profile}
                  onViewMessages={() => setCurrentView("customer-messages")}
                  messageDeepLink={messageDeepLink}
                  onDeepLinkConsumed={() => setMessageDeepLink(null)}
                />
              )}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    );
  }

  // ── Authenticated tradie ──────────────────────────────────────────────────
  if (shouldHoldTradieDashboardForSubscription({ isTradie, businessId, subscription })) {
    return (
      <div className="app-shell">
        <main className="container">
          <Card>
            <h2>Loading account...</h2>
            <p className="muted">
              Please wait while we load your plan and account level.
            </p>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <>
    {showOnboarding && (
      <OnboardingFlow
        profile={profile}
        session={session}
        businessId={businessId}
        onComplete={() => { justCompletedOnboarding.current = true; setShowOnboarding(false); }}
      />
    )}
    <div className="app-shell">
      <Header
        currentView={currentView}
        setCurrentView={setCurrentView}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
        onSignOut={handleSignOut}
        role={role}
        subscription={subscription}
        profile={profile}
        onShowPricing={() => { setCurrentView("tradie"); setShowPricing(true); }}
        creditBalance={creditBalance}
        theme={theme}
      />

      <main className="container">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentView}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
          >
            {currentView === "landing" && (
              <Landing
                setCurrentView={setCurrentView}
                totalRequests={requests.length}
                profile={profile}
              />
            )}

            {currentView === "customer" && (
              <CustomerApp
                onSubmitRequest={handleSubmitRequest}
                session={session}
                role={role}
              />
            )}

            {currentView === "settings" && isTradie && (
              <TradieSettings
                session={session}
                profile={profile}
                onProfileUpdated={() => {
                  // Reload the page to re-run useAuth and pick up the freshly saved profile.
                  // Settings saves are infrequent; a full reload is the simplest reliable refresh.
                  window.location.reload();
                }}
                onOpenGodMode={isGodMode ? () => setGodModeOpen(true) : null}
              />
            )}

            {currentView === "tradie" && (
              <TradieApp
                profile={profile}
                requests={requests}
                leadsLoading={leadsLoading}
                updateRequestStatus={updateRequestStatus}
                createOnsiteQuoteLead={createOnsiteQuoteLead}
                saveQuote={saveQuote}
                unlockLead={unlockLead}
                unlockedLeadIds={unlockedLeadIds}
                creditBalance={creditBalance}
                getCustomerQuoteLink={getCustomerQuoteLink}
                getCustomerInvoiceLink={getCustomerInvoiceLink}
                loadLeadDetails={loadLeadDetails}
                businessId={businessId}
                bookJob={bookJob}
                dismissLead={dismissLead}
                undismissLead={undismissLead}
                dismissedLeadIds={dismissedLeadIds}
                updateLeadStage={updateLeadStage}
                archiveLead={archiveLead}
                unarchiveLead={unarchiveLead}
                updateLeadContactDetails={updateLeadContactDetails}
                subscription={subscription}
                showPricing={showPricing}
                setShowPricing={setShowPricing}
                onGoToSettings={() => setCurrentView("settings")}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </main>
    </div>

    {/* God mode admin panel — drawer is mounted (and overlay shown) only when
        godModeOpen is true. Triggered from Settings → God Mode. The legacy
        floating ⚡ FAB was removed 2026-04-29. */}
    {isGodMode && (
      <GodModePanel
        subscription={subscription}
        onSimulateTier={subscriptionRaw.setSimulatedTier}
        session={session}
        open={godModeOpen}
        setOpen={setGodModeOpen}
      />
    )}

    {/* Mobile bottom nav — only renders on ≤768px breakpoints (CSS-gated).
        Hidden on logged-out / quote-link / direct-quote / reset flows because
        currentView="landing" or token-driven views shouldn't expose nav tabs. */}
    {role && !quoteToken && !invoiceToken && !directBusiness && (
      <MobileBottomNav
        role={role === "business" ? "tradie" : role}
        currentView={currentView}
        setCurrentView={setCurrentView}
      />
    )}

    {/* Cmd+K command palette — global keyboard shortcut. Mounts even when
        logged out so the toggle-theme command works on the landing page. */}
    <CommandPalette
      role={role}
      setCurrentView={setCurrentView}
      onSignOut={role ? handleSignOut : undefined}
    />
    </>
  );
}

// Shown when a session exists but no profile row was found.
// Auto-signs-out after 8 s so the user is never permanently stuck.
function ProfileSetupPending({ onSignOut }) {
  const [countdown, setCountdown] = React.useState(8);

  useEffect(() => {
    const interval = setInterval(() => {
      setCountdown((n) => {
        if (n <= 1) {
          clearInterval(interval);
          onSignOut();
          return 0;
        }
        return n - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [onSignOut]);

  return (
    <Card>
      <h2>Finishing account setup…</h2>
      <p className="muted">
        Your profile is being created. If this takes too long, sign out and try
        again.
      </p>
      <p className="muted">Auto sign-out in {countdown}s.</p>
      <div className="button-row">
        <button type="button" className="btn btn-secondary" onClick={onSignOut}>
          Sign out now
        </button>
      </div>
    </Card>
  );
}
